"use client";

import React, { useMemo, useState } from "react";
import { descendantSlugs, isCoveredBySelection, type CategoryNode } from "@/lib/category-filter";

// De categorieboom als filter (MOW-CATEGORIEFILTER-OKT26): selectievakjes, meerdere tegelijk, met het
// aantal producten erachter zodra de lijst compleet is. Kiezen filtert op de pagina zelf; het zijn
// knoppen en vakjes, geen links, dus Google vindt er geen filteradressen in.
//
// - Een gekozen categorie omvat zijn onderliggende: die staan aan en grijs (niet los te zetten) tot de ouder weer uit is.
// - Zonder aantallen (`counts` is null, de lijst is nog niet compleet) staat elke tak er gewoon in, zonder getal.
// - Elke rij is 44 px hoog op mobiel (zie .cat-tree-label in globals.css).

interface Props {
  nodes: ReadonlyArray<CategoryNode>;
  selected: ReadonlyArray<string>;
  counts: ReadonlyMap<string, number> | null;
  /** De volledige boom, om te weten welke categorieën onder een gekozen ouder vallen. */
  tree: ReadonlyArray<CategoryNode> | null;
  onToggle: (slug: string) => void;
}

export default function CategoryTreeFilter({ nodes, selected, counts, tree, onToggle }: Props) {
  // Wat de bezoeker zelf open of dicht klikte; zonder keuze staat een tak open als er iets onder gekozen is.
  const [handmatig, setHandmatig] = useState<Record<string, boolean>>({});
  const automatischOpen = useMemo(() => {
    const open = new Set<string>();
    if (!tree) return open;
    const visit = (list: ReadonlyArray<CategoryNode>) => {
      for (const n of list) {
        const onder = descendantSlugs(tree, n.slug);
        if (selected.some((s) => s !== n.slug && onder.has(s))) open.add(n.slug);
        visit(n.children);
      }
    };
    visit(nodes);
    return open;
  }, [nodes, selected, tree]);

  const renderNodes = (list: ReadonlyArray<CategoryNode>, pad: string) => (
    <ul className={pad === "" ? "cat-tree" : "cat-tree-children"}>
      {list.map((n) => {
        const heeftKinderen = n.children.length > 0;
        const open = heeftKinderen && (handmatig[n.slug] ?? automatischOpen.has(n.slug));
        const gekozen = selected.includes(n.slug);
        const gedekt = !gekozen && isCoveredBySelection(n.slug, selected, tree);
        const aantal = counts ? counts.get(n.slug) ?? 0 : null;
        return (
          <li key={`${pad}/${n.slug}`} className="cat-tree-item">
            <div className="cat-tree-row">
              {heeftKinderen ? (
                <button
                  type="button"
                  className="cat-tree-toggle"
                  aria-expanded={open}
                  aria-label={`${open ? "Verberg" : "Toon"} de onderdelen van ${n.label}`}
                  onClick={() => setHandmatig((h) => ({ ...h, [n.slug]: !open }))}
                >
                  <span aria-hidden="true" className={open ? "cat-tree-chevron cat-tree-chevron-open" : "cat-tree-chevron"}>▸</span>
                </button>
              ) : (
                <span className="cat-tree-toggle-spacer" aria-hidden="true" />
              )}
              <label className={`cat-tree-label${gekozen ? " cat-tree-label-active" : ""}${gedekt ? " cat-tree-label-covered" : ""}`}>
                <input
                  type="checkbox"
                  className="cat-tree-checkbox"
                  checked={gekozen || gedekt}
                  disabled={gedekt}
                  onChange={() => onToggle(n.slug)}
                />
                <span className="cat-tree-name">{n.label}</span>
                {aantal !== null && <span className="cat-tree-count">({aantal})</span>}
              </label>
            </div>
            {open && renderNodes(n.children, `${pad}/${n.slug}`)}
          </li>
        );
      })}
    </ul>
  );

  return <nav aria-label="Categorieën">{renderNodes(nodes, "")}</nav>;
}
